import { DateTime } from 'luxon';
import React, { useCallback } from 'react';

import { Heading } from '@/components/typography/heading';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import type { ActionLog } from '@/types/logs';
import { buildLogsFilterUrl } from '@/utils/logsUrl';

import InfoRow from './infoRow';
import styles from './logstyles.module.scss';

type Props = {
  selectedLog: ActionLog;
};

const ActionLogDetailView = ( { selectedLog }: Props ) => {
  const [ useLocalTimeZone ] = useLocalZonePreference();

  const formatTimestamp = useCallback( ( timestamp: string | undefined ) => {
    if ( !timestamp ) return undefined;
    const dt = DateTime.fromISO( timestamp, { zone: 'utc' } );
    const zonedDt = useLocalTimeZone ? dt.toLocal() : dt.toUTC();
    return zonedDt.toFormat( 'LLL d, yyyy, h:mm:ss.SSS (ZZZZ)' );
  }, [ useLocalTimeZone ] );

  // Fields not in the strict ActionLog type but commonly populated by the server
  const agentName = ( selectedLog as ActionLog & { agent_name?: string } ).agent_name;
  const callId = ( selectedLog.meta_data?.call_id as string | undefined ) ?? undefined;

  const filterUrl = ( field: Parameters<typeof buildLogsFilterUrl>[0], value: string | undefined ) => ( value ? buildLogsFilterUrl( field, value, 'actionLog' ) : undefined );

  return (
    <div>
      <div>
        {/* Header */}
        <div className={ styles.card }>
          <div className={ styles.header }>
            <Heading as="h2" size="md" className={ styles.title }>Action Log Details</Heading>
            <span className={ styles.statusBadge }>Completed</span>
          </div>
          <InfoRow label="Log ID" value={ selectedLog.id } mono />
        </div>

        {/* Request Details */}
        <div className={ styles.card }>
          <Heading as="h2" size="md" className={ styles.sectionTitle }>Request Details</Heading>
          <InfoRow label="Agent ID" value={ selectedLog.agent_id } mono
            filterHref={ filterUrl( 'agentId', selectedLog.agent_id ) }
          />
          <InfoRow label="Agent Name" value={ agentName }
            filterHref={ filterUrl( 'agentName', agentName ) }
          />
          <InfoRow label="Constellation Name" value={ selectedLog.constellation_name }
            filterHref={ filterUrl( 'constellationName', selectedLog.constellation_name ) }
          />
          <InfoRow label="Constellation Version" value={ selectedLog.constellation_version }
            filterHref={ filterUrl( 'constellationVersion', selectedLog.constellation_version ) }
          />
          <InfoRow label="Call ID" value={ callId } mono />
          <InfoRow label="Start Time" value={ formatTimestamp( selectedLog.start_time ) } />
          <InfoRow label="End Time" value={ formatTimestamp( selectedLog.end_time ) } />
          <InfoRow label="Correlation ID" value={ selectedLog.correlation_id } mono
            filterHref={ filterUrl( 'correlationId', selectedLog.correlation_id ) }
          />
          <InfoRow label="Request ID" value={ selectedLog.request_id } mono
            filterHref={ filterUrl( 'requestId', selectedLog.request_id ) }
          />
          <InfoRow label="Client ID" value={ selectedLog.client_id } mono
            filterHref={ filterUrl( 'clientId', selectedLog.client_id ) }
          />
          <InfoRow label="Component Name" value={ selectedLog.component_name } />
        </div>

        {selectedLog.string_input && (
          <div className={ styles.card }>
            <Heading as="h2" size="md" className={ styles.sectionTitle }>Action Input</Heading>
            <div className={ styles.jsonContainer }>
              <pre className={ styles.jsonContent }>
                {( () => {
                  try {
                    return JSON.stringify( JSON.parse( selectedLog.string_input ), null, 2 );
                  } catch {
                    return selectedLog.string_input;
                  }
                } )()}
              </pre>
            </div>
          </div>
        )}

        {selectedLog.meta_data && (
          <div className={ styles.card }>
            <Heading as="h2" size="md" className={ styles.sectionTitle }>Metadata</Heading>
            <div className={ styles.jsonContainer }>
              <pre className={ styles.jsonContent }>
                {JSON.stringify( selectedLog.meta_data, null, 2 )}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionLogDetailView;
